import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Star, Calendar, Shield, Heart, Users, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { LocalBusinessSchema, BreadcrumbSchema } from '../components/Schema';
import { SEO } from '../components/SEO';
import { OptimizedImage } from '../components/OptimizedImage';
import { homeSEO } from '../data/seoContent';

// Below-the-fold components — lazy load to keep initial bundle small.
// SEOContentBlock pulls in DOMPurify; TestimonialsSlider is rarely visible on first paint.
const TestimonialsSlider = lazy(() =>
  import('../components/TestimonialsSlider').then(m => ({ default: m.TestimonialsSlider }))
);
const SEOContentBlock = lazy(() =>
  import('../components/SEOContentBlock').then(m => ({ default: m.SEOContentBlock }))
);
const InstagramFeed = lazy(() =>
  import('../components/InstagramFeed').then(m => ({ default: m.InstagramFeed }))
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fire the three homepage API requests IMMEDIATELY on module import — before
// React hydrates and before useEffect fires. Combined with the matching
// <link rel="preload" as="fetch"> tags in public/index.html, this eliminates
// the client-side fetch waterfall that was delaying LCP. The <Home> component
// awaits these promises in its effect and never re-fetches.
const prefetch = (path) =>
  fetch(`${API_URL}${path}`, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

const destinationsPromise = prefetch('/api/destinations/');
const packagesPromise = prefetch('/api/packages/');
const homepagePromise = prefetch('/api/content/homepage');

export const Home = () => {
  const [destinations, setDestinations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [instagram, setInstagram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [destData, pkgData, homeData] = await Promise.all([
        destinationsPromise,
        packagesPromise,
        homepagePromise,
      ]);
      if (!alive) return;
      if (destData) setDestinations(destData);
      if (pkgData) setPackages(pkgData);
      if (homeData && homeData.instagram_enabled) {
        setInstagram({
          handle: homeData.instagram_handle || '',
          title: homeData.instagram_title || 'Follow Us on Instagram',
          subtitle: homeData.instagram_subtitle || '',
          posts: Array.isArray(homeData.instagram_posts) ? homeData.instagram_posts : [],
        });
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO 
        title="Kashmir Tour Packages | Velmora Holidays, Srinagar"
        description="Custom Kashmir tour packages from a local Srinagar agency — 10+ years, 5000+ travellers. Gulmarg, Pahalgam, Sonamarg & houseboat stays. Get a free quote today."
        canonical="/"
        keywords="kashmir tour packages, best kashmir tour packages, kashmir holiday packages, kashmir tour operator, srinagar travel agency, gulmarg tour packages, pahalgam packages, best travel agency in kashmir"
        image="https://velmoraholidays.com/images/hero-kashmir-og-1200.jpg"
      />
      {/* Schema Markup */}
      <LocalBusinessSchema />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }]} />
      {/* Hero Section */}
      <section className="relative min-h-[85vh] sm:min-h-[90vh] flex items-center justify-center overflow-hidden">
        <picture>
          <source
            type="image/avif"
            srcSet="/images/hero-kashmir-768.avif 768w, /images/hero-kashmir-1280.avif 1280w"
            sizes="100vw"
          />
          <source
            type="image/webp"
            srcSet="/images/hero-kashmir-768.webp 768w, /images/hero-kashmir-1280.webp 1280w"
            sizes="100vw"
          />
          <img
            src="/images/hero-kashmir-1280.webp"
            alt="Kashmir mountain landscape at sunset"
            fetchPriority="high"
            decoding="async"
            width="1280"
            height="853"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-amber-900/30 to-slate-900/50 z-10"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20 text-center py-16 sm:py-20">
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 tracking-tight leading-tight">
            Kashmir Tour Packages, <span className="text-amber-400">Planned by Locals in Srinagar</span>
          </h1>
          <p className="text-base sm:text-xl md:text-2xl text-gray-100 mb-6 sm:mb-8 max-w-3xl mx-auto px-2">
            Book the Best Kashmir Tour Packages with Expert Guides - Velmora Holidays, Srinagar
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
            <Link to="/packages" className="w-full sm:w-auto">
              <Button size="lg" data-testid="hero-explore-packages-btn" aria-label="Explore Kashmir tour packages" className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-black text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-colors font-bold shadow-lg min-h-[52px]">
                Explore Packages
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" data-testid="hero-plan-trip-btn" aria-label="Plan your custom Kashmir trip" className="w-full sm:w-auto bg-white/95 hover:bg-white text-gray-900 border-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-colors font-bold min-h-[52px]">
                Plan Your Trip
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Kashmir & Company Section */}
      <section className="py-20 bg-white" itemScope itemType="https://schema.org/AboutPage">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <article itemProp="about">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Welcome to <span className="text-amber-700">Kashmir</span></h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Kashmir, often called "Paradise on Earth," is a land of breathtaking natural beauty, snow-capped mountains, pristine valleys, and serene lakes. From the vibrant meadows of Gulmarg to the tranquil waters of Dal Lake, every corner of Kashmir tells a story of unmatched splendor.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6" itemProp="description">
                At <span className="font-semibold text-amber-700" itemProp="name">Velmora Holidays</span>, recognized as the <strong>best travel agency in Kashmir</strong>, we are based in the heart of Srinagar. Our <strong>Kashmir tour expert</strong> team has deep local knowledge and years of experience crafting personalized <strong>Kashmir tour packages</strong> that capture the essence of this paradise. We share the beauty of our homeland with travelers from around the world.
              </p>
              <Link to="/about">
                <Button className="bg-amber-700 hover:bg-amber-800 text-white transition-colors">
                  Learn More About Us
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </article>
            <div className="relative">
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-2xl">
                <picture>
                  <source
                    type="image/avif"
                    srcSet="/images/hero-kashmir-768.avif 768w, /images/hero-kashmir-1280.avif 1280w"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                  <source
                    type="image/webp"
                    srcSet="/images/hero-kashmir-768.webp 768w, /images/hero-kashmir-1280.webp 1280w"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                  <img
                    src="/images/hero-kashmir-768.webp"
                    alt="Beautiful Kashmir landscape - Velmora Holidays travel agency in Srinagar"
                    width="768"
                    height="512"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </picture>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-amber-700 text-white p-6 rounded-lg shadow-xl">
                <p className="text-3xl font-bold">10+</p>
                <p className="text-sm font-medium">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-amber-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Velmora Holidays?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're not just a travel agency - we're your local friends who know Kashmir inside out
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-8 w-8 text-amber-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Local Expertise</h3>
                <p className="text-gray-600">
                  Born and raised in Kashmir, we know every hidden gem and scenic route
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-amber-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Personalized Service</h3>
                <p className="text-gray-600">
                  Customized itineraries tailored to your preferences and travel style
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-amber-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Safety First</h3>
                <p className="text-gray-600">
                  Your comfort and security are our top priorities with 24/7 support
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Explore Kashmir's Wonders</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From snow-capped peaks to serene valleys, discover the destinations that make our Kashmir tour packages unforgettable
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading ? (
              <div className="col-span-4 flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
              </div>
            ) : (
              destinations.slice(0, 8).map((destination) => (
                <Link key={destination.id} to={`/destinations`}>
                  <Card className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 duration-300">
                    <div className="aspect-[3/4] relative overflow-hidden">
                      <OptimizedImage
                        src={destination.image}
                        alt={`${destination.name} - ${destination.tagline}`}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                        width={400}
                        height={533}
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <h3 className="text-2xl font-bold mb-1">{destination.name}</h3>
                        <p className="text-sm text-gray-200">{destination.tagline}</p>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/destinations">
              <Button size="lg" className="bg-amber-700 hover:bg-amber-800 text-white transition-colors">
                View All Destinations
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Packages */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-amber-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Popular Kashmir Tour Packages</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              As the best travel agency in Kashmir, we offer carefully curated Kashmir tour packages designed by our Kashmir tour experts to give you the best experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-3 flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
              </div>
            ) : (
              packages.slice(0, 3).map((pkg) => (
                <Card key={pkg.id} className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-shadow">
                  <div className="aspect-video relative overflow-hidden">
                    <OptimizedImage
                      src={pkg.image}
                      alt={`${pkg.name} - Kashmir tour package`}
                      className="w-full h-full object-cover"
                      width={640}
                      height={360}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-amber-700 font-semibold flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {pkg.duration}
                      </span>
                      <span className="text-2xl font-bold text-gray-900">{pkg.price}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{pkg.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                    <ul className="space-y-2 mb-6">
                      {pkg.highlights?.slice(0, 3).map((highlight, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-center">
                          <Star className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-2">
                      <Link to={`/packages/${pkg.slug || pkg.id}`} className="flex-1" aria-label={`View details for ${pkg.title} Kashmir tour package`}>
                        <Button variant="outline" className="w-full border-amber-700 text-amber-700 hover:bg-amber-50 min-h-[44px]">
                          View Details
                        </Button>
                      </Link>
                      <Link to="/contact" className="flex-1" aria-label={`Book ${pkg.title} Kashmir tour package now`}>
                        <Button className="w-full bg-amber-700 hover:bg-amber-800 text-white transition-colors min-h-[44px]">
                          Book Now
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/packages">
              <Button size="lg" variant="outline" className="border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-white transition-colors">
                View All Packages
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Slider Section */}
      <Suspense fallback={null}>
        <TestimonialsSlider />
      </Suspense>

      {/* Instagram Feed Section (CMS-managed) */}
      {instagram && instagram.posts.length > 0 && (
        <Suspense fallback={null}>
          <InstagramFeed
            handle={instagram.handle}
            title={instagram.title}
            subtitle={instagram.subtitle}
            posts={instagram.posts}
          />
        </Suspense>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-700 via-amber-600 to-orange-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-4xl font-bold mb-6">Ready to Explore Kashmir?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Let us create a personalized itinerary that matches your dreams. Contact us today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-amber-700 hover:bg-gray-100 transition-colors px-8 py-6 text-lg min-h-[52px]">
              <Link to="/contact" aria-label="Contact Velmora Holidays to plan your Kashmir trip">
                Contact Us Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 transition-colors px-8 py-6 text-lg min-h-[52px]">
              <Link to="/packages" aria-label="View all Kashmir tour packages">
                View All Packages
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Velmora Holidays is the Best Travel Agency in Kashmir</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              When searching for the <strong>best travel agency in Kashmir</strong>, you need a partner who understands the region intimately and can craft experiences that go beyond ordinary tourism. Velmora Holidays, based at Khayam Chowk in Srinagar, has been Kashmir's trusted travel partner for over a decade, offering the <strong>best Kashmir tour packages</strong> tailored to your preferences.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Best Kashmir Tour Packages for Every Traveler</h3>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              Our <strong>Kashmir tour packages</strong> are designed by local experts who know every valley, every peak, and every hidden gem in Kashmir. Whether you're looking for <strong>Kashmir honeymoon packages</strong>, <strong>family vacation packages</strong>, or <strong>adventure tours in Kashmir</strong>, we offer the <strong>best Kashmir holiday packages</strong> at competitive prices without compromising on quality.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              From the snow-clad slopes of Gulmarg to the serene meadows of Pahalgam, from the golden valleys of Sonamarg to the tranquil waters of Dal Lake, our <strong>Kashmir tour packages</strong> cover all major destinations. We also include offbeat locations like Doodhpathri, Yousmarg, and seasonal attractions like the famous Tulip Garden.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Why Choose Us as Your Kashmir Tour Operator</h3>
            
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Local Expertise:</strong> Born and raised in Kashmir, we know the best routes, best times to visit, and hidden spots tourists miss</li>
              <li><strong>Customized Packages:</strong> We don't believe in one-size-fits-all. Each Kashmir tour package is personalized to your interests and budget</li>
              <li><strong>Best Prices:</strong> As a local travel agency in Kashmir, we offer the best rates without hidden charges</li>
              <li><strong>24/7 Support:</strong> Our team is available round the clock during your Kashmir tour</li>
              <li><strong>Authentic Experiences:</strong> We connect you with local culture, cuisine, and traditions for genuine Kashmir experiences</li>
              <li><strong>Safety First:</strong> Your comfort and security are our top priorities in all our Kashmir tour packages</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Book the Best Kashmir Tour Packages Today</h3>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              Whether you're planning a romantic honeymoon, a family vacation, or an adventure-filled trip, Velmora Holidays offers the <strong>best Kashmir tour packages</strong> that promise unforgettable memories. As the <strong>best travel agency in Kashmir</strong>, we handle everything from accommodation and transport to sightseeing and activities, ensuring a hassle-free Kashmir holiday.
            </p>

            <p className="text-gray-700 leading-relaxed">
              Contact us today to book your <strong>Kashmir tour package</strong> and experience why thousands of travelers trust Velmora Holidays as their preferred <strong>Kashmir tour operator</strong>. Let us show you Kashmir the way it's meant to be experienced - with local knowledge, genuine hospitality, and attention to every detail.
            </p>
          </div>
        </div>
      </section>

      {/* Additional SEO Content + FAQ */}
      <Suspense fallback={null}>
        <SEOContentBlock {...homeSEO} bg="bg-gradient-to-br from-slate-50 to-amber-50" />
      </Suspense>
    </div>
  );
};